/**
 * Currency Utility Functions
 * 货币工具函数
 *
 * Provides helper functions for currency switching and price formatting.
 * 提供货币切换和价格格式化的辅助函数。
 */

import store from '@/store'

/**
 * Get current currency code
 * 获取当前货币代码
 *
 * @returns {string} - Current currency code / 当前货币代码
 */
export function getCurrentCurrency() {
  return store.state.currencySettings?.currentCurrency || 'AUD'
}

/**
 * Get default currency code (venue's base currency)
 * 获取默认货币代码（餐厅的基础货币）
 *
 * This is the currency in which product prices are stored.
 * 这是产品价格存储时使用的货币。
 *
 * @returns {string} - Default currency code / 默认货币代码
 */
export function getDefaultCurrency() {
  return store.state.currencySettings?.defaultCurrency || 'AUD'
}

/**
 * Get currency symbol for current currency
 * 获取当前货币的符号
 *
 * @returns {string} - Currency symbol / 货币符号
 */
export function getCurrencySymbol() {
  return store.getters['currencySettings/currencySymbol'] || '$'
}

/**
 * Get current currency info object
 * 获取当前货币信息对象
 *
 * @returns {Object} - Currency info object / 货币信息对象
 */
export function getCurrentCurrencyInfo() {
  return (
    store.getters['currencySettings/currentCurrencyInfo'] || {
      code: 'AUD',
      symbol: '$',
      name: 'Australian Dollar',
      flag: '🇦🇺',
      symbolPosition: 'before',
    }
  )
}

/**
 * Get symbol position for current currency
 * 获取当前货币的符号位置
 *
 * @returns {string} - 'before' or 'after' / 'before' 或 'after'
 */
export function getSymbolPosition() {
  return store.getters['currencySettings/symbolPosition'] || 'before'
}

/**
 * Get default (venue base) currency symbol
 * 获取默认（餐厅基础）货币符号
 *
 * Used on order receipt/pay pages where prices are in venue's base currency.
 * 用于订单收据/支付页面，价格以餐厅基础货币显示。
 *
 * @returns {string} - Default currency symbol / 默认货币符号
 */
export function getDefaultCurrencySymbol() {
  return store.getters['currencySettings/defaultCurrencySymbol'] || '$'
}

/**
 * Get default (venue base) currency symbol position
 * 获取默认（餐厅基础）货币符号位置
 *
 * @returns {string} - 'before' or 'after' / 'before' 或 'after'
 */
export function getDefaultSymbolPosition() {
  return store.getters['currencySettings/defaultSymbolPosition'] || 'before'
}

/**
 * Get decimal separator for current currency
 * 获取当前货币的小数点分隔符
 *
 * EUR uses ',' while most other currencies use '.'
 * 欧元使用 ','，其他大多数货币使用 '.'
 *
 * @returns {string} - '.' or ',' / '.' 或 ','
 */
export function getDecimalSeparator() {
  return store.getters['currencySettings/decimalSeparator'] || '.'
}

/**
 * Format a number with the correct decimal separator for the current currency
 * 使用当前货币的正确小数点分隔符格式化数字
 *
 * @param {number|string} value - Number value to format / 要格式化的数字值
 * @param {number} decimals - Number of decimal places / 小数位数
 * @returns {string} - Formatted number string / 格式化后的数字字符串
 */
export function formatDecimalNumber(value, decimals = 2) {
  const formatted = Number(value).toFixed(decimals)
  const separator = getDecimalSeparator()
  if (separator === ',') {
    return formatted.replace('.', ',')
  }
  return formatted
}

/**
 * Convert price from venue's base currency to display currency
 * 将价格从餐厅基础货币转换为显示货币
 *
 * Product prices are stored in the venue's defaultCurrency.
 * This function converts to the user's selected display currency.
 * 产品价格以餐厅的 defaultCurrency 存储。
 * 此函数将其转换为用户选择的显示货币。
 *
 * @param {number} price - Price in venue's base currency / 餐厅基础货币的价格
 * @returns {number} - Price in display currency / 显示货币的价格
 */
export function convertPrice(price) {
  if (!store.getters['currencySettings/convertPrice']) {
    return price
  }
  return store.getters['currencySettings/convertPrice'](price)
}

/**
 * Check if currency conversion is needed
 * 检查是否需要货币转换
 *
 * @returns {boolean} - True if display currency differs from venue's base currency
 */
export function needsConversion() {
  return store.getters['currencySettings/needsConversion'] || false
}

/**
 * Format price with currency symbol
 * 格式化价格（带货币符号）
 *
 * Handles symbol position based on currency:
 * - 'before': $10.00 (USD, AUD, etc.)
 * - 'after': 10.00 € (EUR)
 * 根据货币处理符号位置：
 * - 'before': $10.00（美元、澳元等）
 * - 'after': 10.00 €（欧元）
 *
 * @param {number} price - Price value / 价格值
 * @param {boolean} convert - Whether to convert currency / 是否转换货币
 * @returns {string} - Formatted price string / 格式化后的价格字符串
 */
export function formatPrice(price, convert = true) {
  const symbol = getCurrencySymbol()
  const position = getSymbolPosition()
  const finalPrice = convert ? convertPrice(price) : price
  const formattedNumber = formatDecimalNumber(finalPrice)

  if (position === 'after') {
    return `${formattedNumber} ${symbol}`
  }
  return `${symbol}${formattedNumber}`
}

/**
 * Format price with symbol in correct position
 * 格式化价格，符号在正确位置
 *
 * @param {string} symbol - Currency symbol / 货币符号
 * @param {string|number} priceValue - Formatted price value / 格式化后的价格值
 * @param {string} prefix - Optional prefix like '+' or '-' / 可选前缀如 '+' 或 '-'
 * @returns {string} - Formatted price string / 格式化后的价格字符串
 */
export function formatWithSymbol(symbol, priceValue, prefix = '') {
  const position = getSymbolPosition()
  if (position === 'after') {
    return `${prefix}${priceValue} ${symbol}`
  }
  return `${prefix}${symbol}${priceValue}`
}

/**
 * Format price display for templates
 * 格式化模板中的价格显示
 *
 * Returns an object with symbol, price, and position for flexible template rendering.
 * 返回包含符号、价格和位置的对象，用于灵活的模板渲染。
 *
 * @param {number} price - Price value / 价格值
 * @param {boolean} convert - Whether to convert currency / 是否转换货币
 * @returns {Object} - { symbol, price, position, formatted }
 */
export function getPriceDisplay(price, convert = true) {
  const symbol = getCurrencySymbol()
  const position = getSymbolPosition()
  const finalPrice = convert ? convertPrice(price) : price
  const formattedNumber = formatDecimalNumber(finalPrice)

  return {
    symbol,
    price: formattedNumber,
    position,
    formatted: position === 'after' ? `${formattedNumber} ${symbol}` : `${symbol}${formattedNumber}`,
  }
}

/**
 * Get formatted price with symbol for template use
 * 获取带符号的格式化价格，用于模板
 *
 * @param {number} price - Price value / 价格值
 * @param {Object} options - Options / 选项
 * @param {boolean} options.convert - Whether to convert currency / 是否转换货币
 * @param {string} options.prefix - Prefix like '+' or '-' / 前缀如 '+' 或 '-'
 * @param {number} options.decimals - Number of decimal places / 小数位数
 * @returns {string} - Formatted price string / 格式化后的价格字符串
 */
export function getFormattedPrice(price, options = {}) {
  const { convert = true, prefix = '', decimals = 2 } = options
  const symbol = getCurrencySymbol()
  const finalPrice = convert ? convertPrice(price) : price
  const formattedNumber = formatDecimalNumber(finalPrice, decimals)
  return formatWithSymbol(symbol, formattedNumber, prefix)
}

/**
 * Check if current currency is the default currency
 * 检查当前是否为默认货币
 *
 * @returns {boolean}
 */
export function isDefaultCurrency() {
  const currentCurrency = store.state.currencySettings?.currentCurrency || 'AUD'
  const defaultCurrency = store.state.currencySettings?.defaultCurrency || 'AUD'
  return currentCurrency === defaultCurrency
}

/**
 * Get list of supported currencies
 * 获取支持的货币列表
 *
 * @returns {Array} - Supported currencies list / 支持的货币列表
 */
export function getSupportedCurrencies() {
  return store.state.currencySettings?.supportedCurrencies || []
}

/**
 * Check if multiple currencies are supported
 * 检查是否有多货币支持
 *
 * @returns {boolean}
 */
export function hasMultipleCurrencies() {
  return store.getters['currencySettings/hasMultipleCurrencies'] || false
}

/**
 * Change current currency
 * 切换货币
 *
 * @param {string} currencyCode - Currency code / 货币代码
 */
export function changeCurrency(currencyCode) {
  store.dispatch('currencySettings/changeCurrency', currencyCode)
}

/**
 * Reset currency to default
 * 重置货币到默认值
 */
export function resetCurrency() {
  store.commit('currencySettings/resetCurrency')
}

/**
 * Get exchange rate for a specific currency
 * 获取特定货币的汇率
 *
 * @param {string} currencyCode - Currency code / 货币代码
 * @returns {number} - Exchange rate / 汇率
 */
export function getExchangeRate(currencyCode) {
  return store.state.currencySettings?.exchangeRates?.[currencyCode] || 1
}
